import React from "react"
import { createSelector, createStructuredSelector } from "reselect"
import moment from "moment"
import { formValueSelector } from "redux-form/immutable"

// components
import ActionColumnGrid from "app/static/frontend/sheets/components/cells/ActionColumnGrid"
import AccessCell from "app/static/frontend/sheets/components/resource-tables/AccessCell"
import ArchiveAction from "sheets/components/cells/action-buttons/ArchiveAction"
import DuplicateDashboardAction from "sheets/components/cells/action-buttons/DuplicateDashboardAction"
import PublicLinkAction from "app/static/frontend/sheets/components/cells/action-buttons/PublicLinkAction"

// selectors
import {
    selectDashboardsStore,
    selectDashboardsValue,
    selectDashboardTemplatesHasMore,
    selectDashboardTemplatesLoading,
    selectDashboardTemplateObjsForCreateForm,
} from "app/static/frontend/dashboards/selectors"
import { selectIsLimitedToTeamsEnabled } from "shared/userdata/userdata-selectors"
import { selectCustomRegions } from "app/static/frontend/subfeatures/RegionSelectionPage/selectors"

// constants
import sheetConstants from "app/static/frontend/sheets/constants"

// form
import { DASHBOARD_FORM_KEY, DASHBOARD_EDIT_FORM_KEY } from "shared/imports/sharedConstants"

// selectors for the create Dashboard/Widget modals
const selectIsCreateDashboardModalOpen = createSelector(selectDashboardsStore, (state) =>
    state.get("createDashboardModalOpen"),
)
const selectDashboardForm = createSelector(selectDashboardsStore, (state) => state.get("createDashboardForm"))
const selectDashboardFormPage = createSelector(selectDashboardForm, (state) => state.get("page"))
const selectExternalSlug = createSelector(selectDashboardForm, (state) => state.get("external_slug"))

const createDashboardFormSelector = (field) => (state) => formValueSelector(DASHBOARD_FORM_KEY)(state, field)

const editDashboardFormSelector = (field) => (state) => formValueSelector(DASHBOARD_EDIT_FORM_KEY)(state, field)

const getDefaultExternalRegion = (customRegions) => {
    // If we are creating a Dashboard, the initial value for external_regions should default
    // to the user's current region based on Userdata.

    // Get the user's default SupportedRegion
    const defaultSupportedRegion = DjangIO.app.models.SupportedRegion.items().find(
        (region) => region.abbrev === Userdata.current_regions[0],
    )

    // Get the user's current CustomRegionSelection (if it exists)
    const currentPreset = customRegions.find((customRegion) => customRegion.name === Userdata.current_preset_name)

    // If the user is in a CustomRegionSelection, use that custom region, else use a default SupportedRegion.
    return Userdata.current_preset_name && currentPreset
        ? {
              label: Userdata.current_preset_name,
              value: DjangIO.app.userdata.models.CustomRegionSelection.foreignKey(currentPreset.id),
          }
        : {
              label: defaultSupportedRegion.region_name,
              value: defaultSupportedRegion.value,
          }
}

const selectSelectedTemplate = createDashboardFormSelector("selectedTemplate")
const selectInitialValues = createSelector(selectExternalSlug, selectCustomRegions, (external_slug, customRegions) => ({
    external_slug,
    is_external: false,
    limited_to_teams: Userdata.default_limited_to_teams,
    selectedTemplate: DjangIO.app.widgets.templates.DashboardTemplates.blank.value,
    external_regions: getDefaultExternalRegion(customRegions),
}))

export const dashboardFormConnection = createStructuredSelector({
    dashboardTemplates: selectDashboardTemplateObjsForCreateForm,
    external_slug: selectExternalSlug,
    hasMoreTemplates: selectDashboardTemplatesHasMore,
    initialValues: selectInitialValues,
    isLoadingTemplates: selectDashboardTemplatesLoading,
    externalDownloadsDisabled: createDashboardFormSelector("externalDownloadsDisabled"),
    is_external: createDashboardFormSelector("is_external"),
    external_password: createDashboardFormSelector("external_password"),
    passwordRequired: createDashboardFormSelector("passwordRequired"),
    page: selectDashboardFormPage,
    selectedTemplate: selectSelectedTemplate,
    customRegions: selectCustomRegions,
})

export const selectIsCreator = createSelector(
    selectDashboardsValue("creator"),
    (creatorUri) => Userdata.id === DjangIO.app.userdata.models.QuorumUser.idFromResourceUri(creatorUri),
)

export const dashboardEditFormConnection = createStructuredSelector({
    _externalDownloadsDisabled: editDashboardFormSelector("externalDownloadsDisabled"),
    _is_external: editDashboardFormSelector("is_external"),
    _passwordRequired: editDashboardFormSelector("passwordRequired"),
    _externalPassword: editDashboardFormSelector("external_password"),
    initialValues: (_, props) => {
        const customRegions = props.customRegions
        const regions = props.external_regions
        let selectedRegion
        if (props.external_custom_region_selection && customRegions.length) {
            // custom region
            const selectedCustomRegion = customRegions.find(
                (customRegion) => customRegion.resource_uri === props.external_custom_region_selection,
            )
            if (selectedCustomRegion) {
                selectedRegion = {
                    label: selectedCustomRegion.name,
                    value: selectedCustomRegion.resource_uri,
                }
            }
        } else if (regions.length > 1 && customRegions.length) {
            // handle external_regions that are custom region but setup before external_custom_region_selection was implemented
            // use includes because new regions could be added to the selected custom region
            // and not reflected on the dashboard external_regions value
            let selectedCustomRegion = customRegions.filter((customRegion) =>
                customRegion.region_values.toString().includes(regions.toString()),
            )
            if (selectedCustomRegion.length > 1) {
                // if multiple custom regions have the same region_values, use the one that has exact same region_values
                selectedCustomRegion = selectedCustomRegion.filter(
                    (customRegion) => customRegion.region_values.toString() === regions.toString(),
                )
            }
            if (selectedCustomRegion[0]) {
                selectedRegion = {
                    label: selectedCustomRegion[0].name,
                    value: selectedCustomRegion[0].resource_uri,
                }
            }
        } else if (regions.length === 1) {
            // predefined SupportedRegion
            selectedRegion = {
                label: DjangIO.app.models.SupportedRegion.by_value(regions[0]).region_name,
                value: regions[0],
            }
        }

        if (!selectedRegion) {
            // When there is no matching custom region, or when no previous external region was selected
            selectedRegion = getDefaultExternalRegion(customRegions)
        }

        return {
            id: props.dashboardId,
            limited_to_teams: props.limitedToTeams,
            is_limited_to_creator: props.isLimitedToCreator,
            external_slug: props.external_slug,
            is_external: props.is_external,
            passwordRequired: !!props.external_password,
            external_password: props.external_password,
            externalDownloadsDisabled: props.externalDownloadsDisabled,
            external_regions: selectedRegion,
        }
    },
    params: (_, props) => ({
        dashboardId: props.dashboardId,
    }),
})

const actionColumnButtons = [
    {
        name: "Archive",
        icon: "archive",
        requiredFields: ["id"],
        action: ArchiveAction,
    },
    {
        name: "Duplicate",
        icon: "copy",
        requiredFields: ["id"],
        action: DuplicateDashboardAction,
    },
]

// selector for the columns in the DashboardTable component
// app/static/frontend/dashboards/components/DashboardTable
export const selectColumns = createSelector(selectIsLimitedToTeamsEnabled, (isLimitedToTeamsEnabled) => [
    {
        name: "Actions",
        field: ["id"],
        display: (id, additionalArgs) => (
            <ActionColumnGrid
                buttons={actionColumnButtons}
                fields={{
                    dataType: DjangIO.app.models.QuorumDataType.dashboard.value,
                    // id of the Dashboard in the row for the ArchiveAction
                    id,
                    // additionalArgs are necssary because they pass down the tableId
                    // (which is necessary for our call to removeResourceTableRow)
                    // this value comes from CREATE_BLANK_SHEET in app/static/frontend/sheets/sheet-reducer
                    ...additionalArgs,
                }}
                format={sheetConstants.ACTION_COLUMN_GRID_FORMAT.DEFAULT}
            />
        ),
        width: 240,
    },
    {
        name: "Creator",
        field: "_extra",
        display: (extra) => extra && extra.get("creator_full_name"),
        resourceTableEnableFiltering: true,
        resourceTableFilterOn: "creator_fullname",
        width: 185,
    },
    {
        name: "Created",
        field: "created",
        display: (date) => moment(date).local().format(sheetConstants.DATE_TIME_FORMAT),
        resourceTableEnableFiltering: true,
        resourceTableEnableSorting: true,
        width: 210,
    },
    {
        name: "Updated",
        field: "updated",
        display: (date) => moment(date).local().format(sheetConstants.DATE_TIME_FORMAT),
        resourceTableEnableFiltering: true,
        resourceTableEnableSorting: true,
        width: 210,
    },
    ...(isLimitedToTeamsEnabled
        ? [
              {
                  name: "Access",
                  field: ["limited_to_teams", "is_limited_to_creator"],
                  display: (limitedToTeams, isCreator) => (
                      <AccessCell cellValue={limitedToTeams} isCreator={isCreator} />
                  ),
                  width: 190,
              },
          ]
        : []),
    {
        name: "Shared",
        field: ["is_external", "external_slug"],
        display: (isPublic, slug) => <PublicLinkAction isPublic={isPublic} slug={slug} type="dashboard" />,
        resourceTableDisableCellLink: true,
        resourceTableEnableFiltering: true,
        resourceTableFilterOn: "is_external",
        width: 130,
    },
])
export const dashboardTableConnection = createStructuredSelector({
    columns: selectColumns,
    createDashboardModalPage: selectDashboardFormPage,
    isCreateDashboardModalOpen: selectIsCreateDashboardModalOpen,
})
